<template>
  <router-view />
</template>

<script setup></script>

<style lang="scss">
// ============================================================
// Global Element Plus skin — "Empire Blueprint"
// Slate navy panels, hairline frames, squared corners, brass signal.
// ElMessageBox lives in styles/theme.scss to keep one source.
// ============================================================

#app {
  width: 100%;
  height: 100%;

  .el-backtop {
    width: 44px;
    height: 44px;
    background: $carbon;
    border: 1px solid $rule-mid;
    border-radius: $r-md;
    box-shadow: $shadow-md;
    transition: all $dur-fast $ease-out;

    .el-icon {
      color: $accent-light;
    }

    &:hover {
      background: $accent;
      border-color: $accent;
      transform: translateY(-2px);

      .el-icon {
        color: $text-on-accent;
      }
    }
  }
}

// ---------------------------------------------------------
// Dialog overlay
// ---------------------------------------------------------
.el-overlay:has(.carrom-dialog),
.el-overlay-dialog:has(.carrom-dialog) {
  background-color: rgba($scrim, 0.8) !important;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

// ---------------------------------------------------------
// Shared dialog shell (login / pay)
// ---------------------------------------------------------
.el-dialog.carrom-dialog {
  background: $carbon !important;
  border: 1px solid $rule-mid !important;
  border-top: 2px solid $accent !important;
  border-radius: $r-md !important;
  padding: 0 !important;
  overflow: hidden;
  box-shadow: $shadow-lg;

  .el-dialog__header {
    margin: 0;
    padding: 20px 26px 16px;
    border-bottom: 1px solid $rule;
    background: $dark-50;
  }

  .el-dialog__title {
    font-family: $font-display;
    font-size: 20px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: $text-strong;
  }

  .el-dialog__headerbtn {
    top: 16px;
    right: 18px;
    width: 30px;
    height: 30px;
    background: transparent;
    border: 1px solid $rule-mid;
    border-radius: $r-sm;
    transition: all $dur-fast $ease-out;

    .el-dialog__close {
      color: $text-muted;
      font-size: 15px;
    }

    &:hover {
      background: rgba($accent, 0.12);
      border-color: $accent;

      .el-dialog__close {
        color: $accent-light;
      }
    }
  }

  .el-dialog__body {
    padding: 26px;
    color: $text-body;
  }

  // ---- Form controls ----
  .el-input__wrapper,
  .el-select__wrapper,
  .el-textarea__inner {
    background: $dark-900 !important;
    box-shadow: none !important;
    border: 1px solid $rule-mid !important;
    border-radius: $r-sm !important;
    padding: 10px 16px;
    transition: border-color $dur-fast $ease-out;

    &:hover {
      border-color: $line-strong !important;
    }

    &.is-focus,
    &:focus-within {
      border-color: $accent !important;
      box-shadow: 0 0 0 3px rgba($accent, 0.2) !important;
    }
  }

  .el-input__inner,
  .el-textarea__inner,
  .el-select__placeholder {
    color: $text-strong !important;
    font-family: $font-body;

    &::placeholder {
      color: $text-light !important;
    }
  }

  .el-form-item__label {
    color: $text-muted !important;
    font-family: $font-alt;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }

  .el-form-item__error {
    font-family: $font-body;
    font-size: 12px;
    letter-spacing: 0.02em;
    color: $hot-light !important;
  }
}

// ---------------------------------------------------------
// Select dropdown (rendered in a popper, outside the dialog)
// ---------------------------------------------------------
.el-select-dropdown {
  background: $carbon !important;
  border: 1px solid $rule-mid !important;
  border-radius: $r-md !important;
  box-shadow: $shadow-lg !important;

  .el-select-dropdown__item {
    font-family: $font-body;
    font-size: 14px;
    color: $text-body;
    border-radius: $r-sm;

    &.is-hovering,
    &:hover {
      background: rgba($accent, 0.08) !important;
      color: $text-strong !important;
    }

    &.is-selected {
      color: $accent-light !important;
      font-weight: 600;
    }
  }

  .el-popper__arrow::before {
    background: $carbon !important;
    border-color: $rule-mid !important;
  }
}

// ---------------------------------------------------------
// Toast messages
// ---------------------------------------------------------
.el-message {
  background: $carbon !important;
  border: 1px solid $rule-mid !important;
  border-left: 3px solid $accent !important;
  border-radius: $r-sm !important;
  box-shadow: $shadow-lg !important;

  .el-message__content {
    font-family: $font-body !important;
    font-size: 14px !important;
    letter-spacing: 0.01em;
    color: $text-strong !important;
  }

  &.el-message--error {
    border-left-color: $danger !important;
  }

  &.el-message--success {
    border-left-color: $success !important;
  }

  &.el-message--warning {
    border-left-color: $warning !important;
  }
}
</style>
